/**
 * Mux / consent tracing. On by default for webpack **development** builds (`ASVP_MUX_DEBUG=1` from
 * DefinePlugin). Override any time: `window.__ASVP_MUX_DEBUG__ = true` (or `false` to force off).
 */

import type { MuxConfig } from '@top/player-block-web';

import { getMuxConsentDenialHint, getMuxConsentSnapshotForDebug } from './muxConsent';
import { getMuxKeySnapshotForDebug } from './muxKey';

declare global {
    interface Window {
        /** Set to `true` to log Mux consent / key / setup decisions (any build). */
        __ASVP_MUX_DEBUG__?: boolean;
    }
}

function envStr(name: string): string | undefined {
    const v = (process.env as Record<string, string | undefined>)[name];
    return typeof v === 'string' ? v : undefined;
}

export function isMuxDebugEnabled(): boolean {
    if (typeof window !== 'undefined') {
        if (window.__ASVP_MUX_DEBUG__ === true) {
            return true;
        }
        if (window.__ASVP_MUX_DEBUG__ === false) {
            return false;
        }
    }
    if (envStr('ASVP_MUX_DEBUG') === '1') {
        return true;
    }
    return process.env.NODE_ENV === 'development';
}

export function muxDebugLog(...args: unknown[]): void {
    if (!isMuxDebugEnabled()) {
        return;
    }
    console.log('[asvp mux]', ...args);
}

/** Throttled — `buildSetupMuxConfig` runs often (e.g. every 100ms from the consent epic). */
let muxDebugThrottleUntil = 0;
const MUX_DEBUG_THROTTLE_MS = 1500;

export function muxDebugLogThrottled(...args: unknown[]): void {
    if (!isMuxDebugEnabled()) {
        return;
    }
    const now = Date.now();
    if (now < muxDebugThrottleUntil) {
        return;
    }
    muxDebugThrottleUntil = now + MUX_DEBUG_THROTTLE_MS;
    console.log('[asvp mux]', ...args);
}

let lastSetupMuxDebugSignature = '';

/**
 * Logs when the outcome or failure reason changes (avoids spam while consent epic polls).
 */
export function logSetupMuxDecision(config: MuxConfig, reason: string): void {
    if (!isMuxDebugEnabled()) {
        return;
    }
    const sig = `${String(config.enabled)}:${reason}`;
    if (lastSetupMuxDebugSignature === sig) {
        return;
    }
    lastSetupMuxDebugSignature = sig;
    console.log('[asvp mux] buildSetupMuxConfig', {
        enabled: config.enabled,
        reason,
        denialHint: config.enabled ? '' : getMuxConsentDenialHint(),
        environmentKeyLength: config.environmentKey ? String(config.environmentKey).length : 0,
        consent: getMuxConsentSnapshotForDebug(),
        key: getMuxKeySnapshotForDebug(),
    });
}

let lastPlayMuxDebugSignature = '';

export function logPlayMuxDecision(config: MuxConfig, reason: string): void {
    if (!isMuxDebugEnabled()) {
        return;
    }
    const sig = `${String(config.enabled)}:${reason}`;
    if (lastPlayMuxDebugSignature === sig) {
        return;
    }
    lastPlayMuxDebugSignature = sig;
    console.log('[asvp mux] buildMuxConfig (play)', {
        enabled: config.enabled,
        reason,
        denialHint: config.enabled ? '' : getMuxConsentDenialHint(),
        environmentKeyLength: config.environmentKey ? String(config.environmentKey).length : 0,
        consent: getMuxConsentSnapshotForDebug(),
        key: getMuxKeySnapshotForDebug(),
    });
}
